import { useSelector } from "react-redux";
import { useUserData } from "src/hooks/redux";
import { useSurveyAnswers } from "src/hooks/survey/answers";
import type { CoreReduxState } from "src/utils/redux/internal";
import { useAuthSnapshot } from "@noom/hive-auth";
import { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { UserDataState } from "@utils/redux/slices/userData";
import { PaymentEnrollmentFormState } from "@utils/redux/slices/paymentEnrollmentForm";

/**
 * Internal hook to access enrollment information from the payment enrollment form state.
 *
 * @internal
 * @returns The enrollment information containing user details like name and email
 *
 * @remarks
 * Loading directly rather than using useEnrollment hook to avoid pulling in
 * the rather large intl tel validation package across the entire app.
 */
const useEnrollmentInfo = () => {
  const { enrollmentInfo } = useSelector(
    (state: CoreReduxState) => state.paymentEnrollmentForm
  );

  return enrollmentInfo;
};

/**
 * Hook to get the user's name from multiple possible sources with fallback priority.
 *
 * @returns The user's name string, with fallbacks in this priority order:
 * 1. Survey answer: `name`
 * 2. Survey answer: `welcomeName`
 * 3. Survey answer: `firstName`
 * 4. Survey answer: `legalFirstName`
 * 5. Enrollment form: `enrollmentInfo.name`
 * 6. User data: `userData.name`
 * 7. Empty string as final fallback
 *
 */
export function getUserName(
  surveyAnswers: SurveyAnswersState,
  enrollmentInfo?: PaymentEnrollmentFormState["enrollmentInfo"],
  userData?: UserDataState
) {
  const { name, welcomeName, firstName, legalFirstName } = surveyAnswers;

  return (
    name ||
    welcomeName ||
    firstName ||
    legalFirstName ||
    enrollmentInfo?.name ||
    userData?.name ||
    ""
  );
}

export const useUserName = (): string => {
  const surveyAnswers = useSurveyAnswers();
  const userData = useUserData();
  const enrollmentInfo = useEnrollmentInfo();

  return getUserName(surveyAnswers, enrollmentInfo, userData);
};

/**
 * Hook to get the user's email address from available data sources with fallback.
 *
 * @returns The user's email address, with fallbacks in this priority order:
 * 1. Enrollment form: `enrollmentInfo.email`
 * 2. User data: `userData.email`
 * 3. "Noom" as final fallback (appears to be used as a display name when email is unavailable)
 *
 */
export const useUserEmail = (): string => {
  const authSnapshot = useAuthSnapshot();
  const userData = useUserData();
  const enrollmentInfo = useEnrollmentInfo();

  return (
    authSnapshot?.email || enrollmentInfo?.email || userData.email || "Noom"
  );
};
